// =============================================================================
// Return (§5.2). Scan the copy — no member selection, ever.
//
// The copy already knows who holds it. Asking staff to pick a student first
// adds a step that can only introduce error.
//
// The scanner control is built ONCE and never re-mounted. Rebuilding it on
// every render tore the live <video> out of the page while the camera was still
// decoding into it, which is why a desktop webcam return ended in a loop of
// "not currently on loan" — the detached decoder kept re-reading the label of
// the book that had just been returned.
// =============================================================================

import { el, mount, formatDate } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { findCopyByCode, activeLoanForCopy, returnCopy, normaliseCode } from '../lib/api.js';
import { createCodeInput } from '../lib/code-input.js';
import { emitDataChanged } from '../lib/events.js';

export function returnScreen(root) {
  let pending = null;   // the copy awaiting confirmation
  let session = [];     // returns completed without leaving the screen
  let busy = false;

  const view = el('div', { class: 'screen' });
  mount(root, view);

  const codeInput = createCodeInput({
    label: T.scanToReturn,
    actionLabel: T.find,
    onSubmit: lookup,
    autoFocus: true,
    // One scan, one book, one confirmation. The camera has no more work to do
    // until staff act on the card, so it closes itself.
    closeOnScan: true,
  });

  // Static skeleton: only the two boxes below the scanner ever change.
  const notices = el('div', { class: 'notices' });
  const pendingBox = el('div', {});
  const sessionBox = el('div', {});

  mount(view,
    el('h1', { class: 'screen-title' }, T.returnTitle),
    codeInput.root,
    notices,
    pendingBox,
    sessionBox,
  );

  function render() {
    mount(pendingBox, pending ? renderPending() : null);
    mount(sessionBox, session.length
      ? el('section', { class: 'card' },
          el('h2', { class: 'card-title' }, T.returnedToday),
          ...session.map((r) => el('div', { class: 'loan-row' },
            el('span', { class: 'loan-title' }, r.title),
            el('span', { class: 'muted' }, r.member_name),
          )),
        )
      : null);
  }

  function renderPending() {
    const overdue = pending.days_overdue > 0;
    return el('section', { class: `card ${overdue ? 'card-warn' : ''}` },
      el('h2', { class: 'card-title' }, pending.title),
      el('p', { class: 'muted' }, pending.copy_code),
      el('dl', { class: 'detail' },
        el('dt', {}, T.borrowedBy), el('dd', {}, pending.member_name),
        el('dt', {}, T.wasDue), el('dd', {}, formatDate(pending.due_at)),
      ),
      el('p', { class: overdue ? 'pill pill-danger' : 'pill' },
        overdue ? T.daysOverdue(pending.days_overdue) : T.onTime),
      // The same confirm-then-cancel pair, in the same order and at the same
      // size, as every other confirmation in the app — including the one on a
      // member's own screen, which returns exactly the same book.
      el('div', { class: 'form-actions' },
        el('button', {
          class: 'btn btn-primary', disabled: busy,
          onClick: confirm,
        }, busy ? T.returning : T.confirmReturn),
        el('button', { class: 'btn', onClick: () => { pending = null; render(); } }, T.cancel),
      ),
    );
  }

  // One message at a time. Stacked identical errors were the visible symptom of
  // the scanner loop and say nothing that the first one did not.
  function flash(message, kind = 'error') {
    const note = el('p', { class: kind === 'error' ? 'error' : 'notice', role: 'alert' }, message);
    mount(notices, note);
    setTimeout(() => note.remove(), 5000);
  }

  async function lookup(raw) {
    const code = normaliseCode(raw);
    if (!code) return;
    try {
      const copy = await findCopyByCode(code);
      if (!copy) { flash(`Unknown book code: ${code}`); return; }
      if (copy.status !== 'on_loan') {
        flash(copy.status === 'available'
          ? `${copy.titles?.title ?? code} is already back on the shelf — nothing to return.`
          : `That copy is marked ${copy.status}.`);
        return;
      }
      // Who holds it, and is it late? The copy row alone does not say.
      const loan = await activeLoanForCopy(copy.id);
      if (!loan) { flash('That book is not currently on loan.'); return; }

      pending = {
        copy_id: copy.id,
        copy_code: copy.copy_code,
        title: loan.title,
        member_name: loan.member_name,
        due_at: loan.due_at,
        days_overdue: loan.days_overdue,
      };
      render();
    } catch (err) {
      flash(err.message);
    }
  }

  async function confirm() {
    if (!pending || busy) return;
    const code = pending.copy_code;
    busy = true; render();
    try {
      const result = await returnCopy(pending.copy_id);
      session.unshift({ title: result.title, member_name: result.member_name });
      emitDataChanged();
      // The book is available from this moment; mute its label so the camera,
      // still pointed at it, does not report it as "not on loan".
      codeInput.remember(code);
      pending = null;
      busy = false;
      render();
      // §5.2 step 4: if this return cleared the block, say so explicitly.
      if (result.block_cleared) flash(`${result.member_name}: ${T.blockCleared}`, 'notice');
      else flash(`${T.returned}: ${result.title}`, 'notice');
      codeInput.focus();
    } catch (err) {
      busy = false;
      render();
      flash(err.message);
    }
  }

  render();
  return { destroy: () => codeInput.stop() };
}
